import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, useOutletContext } from 'react-router';

import { useTypedParams } from '@sorare/routing';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PlayerPageAsideLayout } from '@sorare/core/src/components/layout/PlayerPageAsideLayout';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import type { Props as TabsProps } from '@sorare/core/src/components/seo/Player/Tabs';
import {
  PlayerPageTabs,
  Tabs,
} from '@sorare/core/src/components/seo/Player/Tabs';
import {
  ANY_SPORT_PLAYERS_SLUG,
  ANY_SPORT_PLAYERS_SLUG_FIXTURES,
  ANY_SPORT_PLAYERS_SLUG_HISTORY,
  ANY_SPORT_PLAYERS_SLUG_WATCHLISTS,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useConversionCredits } from '@sorare/core/src/hooks/useConversionCredits';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { isType } from '@sorare/core/src/lib/gql';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import {
  Content as PlayerMarketDrawerContent,
  Variant as PlayerMarketDrawerContentVariant,
} from '@sorare/marketplace/src/components/player/PlayerMarketDrawer/Content';

import { AnyPlayerHeader } from 'components/player/Header';
import { MobilePlayerPageFooter } from 'components/player/MobilePlayerPageFooter';

import type {
  AnyPlayerLayoutQuery,
  AnyPlayerLayoutQueryVariables,
} from './__generated__/layout.graphql';
import type { PageParams } from './__generated__/routeParams';

const SEO_ANY_PLAYER_QUERY = gql`
  query AnyPlayerLayoutQuery($slug: String!, $onlyPrimary: Boolean = false) {
    anyPlayer(slug: $slug) {
      slug
      sport
      displayName
      avatarPictureUrl
      cardSupply {
        limited
      }
      commonPlayer(rarity: common) {
        id
      }
      ...AnyPlayerHeader_anyPlayer
      ...MobilePlayerPageFooter_anyPlayer
      ... on Player {
        slug
        pastGames(first: 1) {
          nodes {
            id
          }
        }
      }
    }
  }
  ${AnyPlayerHeader.fragments.anyPlayer}
  ${MobilePlayerPageFooter.fragments.anyPlayer}
` as TypedDocumentNode<AnyPlayerLayoutQuery, AnyPlayerLayoutQueryVariables>;

type ContextType = {
  player: AnyPlayerLayoutQuery['anyPlayer'];
  slug: string;
};

export const useLayoutData = () => {
  return useOutletContext<ContextType>();
};

export const loader = queryLoader(
  SEO_ANY_PLAYER_QUERY,
  ({ slug }: PageParams) => ({ slug })
);

export const PlayersSlugLayout = () => {
  const { slug } = useTypedParams<PageParams>();
  const { sport, generateSportContextPath } = useSportContext();
  const { data } = useQueryLoader<AnyPlayerLayoutQuery>();
  const { conversionCredits } = useConversionCredits({
    sport,
  });

  useTitleAndDescription(
    metadatas.playerOverview.title,
    metadatas.playerOverview.description,
    {
      player: data?.anyPlayer.displayName,
    },
    {
      img: data?.anyPlayer.avatarPictureUrl,
    }
  );

  const player = data?.anyPlayer;
  const commonPlayer = player?.commonPlayer;
  const tabs: TabsProps['tabs'] = useMemo(() => {
    return {
      [PlayerPageTabs.PERFORMANCE]: {
        to: generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
          params: { slug },
        }),
      },
      [PlayerPageTabs.FIXTURES]: {
        to: generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_FIXTURES, {
          params: { slug },
        }),
      },
      [PlayerPageTabs.HISTORY]: {
        to: generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_HISTORY, {
          params: { slug },
        }),
      },
      ...(commonPlayer
        ? {
            [PlayerPageTabs.WATCHLISTS]: {
              to: generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_WATCHLISTS, {
                params: { slug },
              }),
            },
          }
        : {}),
    };
  }, [slug, commonPlayer, generateSportContextPath]);

  const hasEnoughSEOData =
    player.cardSupply.length > 0 ||
    (isType(player, 'Player') && player.pastGames.nodes.length > 0);
  const canUseConversionCredit = conversionCredits?.some(cc => !cc.disabled);

  return (
    <>
      <PageHeader text={player.displayName} />
      {!hasEnoughSEOData && <NoIndex />}
      <PlayerPageAsideLayout
        aside={[
          <PlayerMarketDrawerContent
            key={player.slug}
            playerSlug={player.slug}
            sport={player.sport}
            canUseConversionCredit={canUseConversionCredit}
            featurePrimaryOffer
            withFooter
            variant={PlayerMarketDrawerContentVariant.PLAYER_PAGE}
          />,
        ]}
        footer={<MobilePlayerPageFooter anyPlayer={player} />}
      >
        <Vertical gap={0}>
          <AnyPlayerHeader player={player} />
          <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
            <Outlet context={{ player, slug } satisfies ContextType} />
          </ContentWithStickyHeader>
        </Vertical>
      </PlayerPageAsideLayout>
    </>
  );
};

export default PlayersSlugLayout;
